import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/ApiService';
import type { BiltEvent } from '../../types/event';
import type { CheckinRegistrationRow } from '../../types/event';
import PageLoader from '../ui/PageLoader';
import Footer from '../Footer';
import Button from '../ui/Button';
import PageBackLink from '../ui/PageBackLink';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Registered',
  confirmed: 'Confirmed',
  checked_in: 'Checked in',
  no_show: 'No-show',
  withdrawn: 'Withdrawn',
};

const STATUS_STYLES: Record<string, string> = {
  submitted: 'bg-orange-950/30 text-orange-200 border-orange-800/50',
  confirmed: 'bg-emerald-950/40 text-emerald-200 border-emerald-800/50',
  checked_in: 'bg-emerald-950/50 text-emerald-100 border-emerald-700/60',
  no_show: 'bg-[#141414]/80 text-gray-400 border-white/[0.10]',
  withdrawn: 'bg-red-950/40 text-red-300 border-red-800/40',
};

const EventCheckin: React.FC = () => {
  const { eventId: eventIdParam } = useParams<{ eventId: string }>();
  const eventId = Number(eventIdParam);

  const [event, setEvent] = useState<BiltEvent | null>(null);
  const [rows, setRows] = useState<CheckinRegistrationRow[]>([]);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!eventId || isNaN(eventId)) return;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const evt = await apiService.getEvent(eventId);
        setEvent(evt);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load event');
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId]);

  useEffect(() => {
    if (!eventId || isNaN(eventId)) return;
    (async () => {
      try {
        setSearching(true);
        setError(null);
        const list = await apiService.getEventCheckinList(eventId, debouncedQuery || undefined);
        setRows(list);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load check-in list');
      } finally {
        setSearching(false);
      }
    })();
  }, [eventId, debouncedQuery]);

  const stats = useMemo(() => {
    const checked = rows.filter((r) => r.status === 'checked_in').length;
    const noShow = rows.filter((r) => r.status === 'no_show').length;
    return { total: rows.length, checked, noShow, pending: rows.length - checked - noShow };
  }, [rows]);

  const handleStatus = async (registrationId: number, status: 'checked_in' | 'no_show' | 'confirmed') => {
    try {
      setBusyId(registrationId);
      setError(null);
      await apiService.updateRegistrationCheckin(registrationId, status);
      setRows((prev) =>
        prev.map((r) => (r.id === registrationId ? { ...r, status } : r))
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Check-in update failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading && !event) {
    return <PageLoader message="Loading check-in…" />;
  }

  if (error && !event) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center px-4">
        <p className="text-red-400 mb-4 text-center max-w-md">{error}</p>
        <PageBackLink label="Back to Organizer" to="/organizer" className="mb-4" />
        <Footer />
      </div>
    );
  }

  if (!event) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
        <PageBackLink label="Back to event admin" to={`/organizer/events/${eventId}`} className="mb-4" />

        <div className="mb-6">
          <p className="kicker mb-2">Event day</p>
          <h1 className="heading-display tracking-tight">{event.title}</h1>
          <p className="text-muted text-sm mt-2">Check in vehicles as they arrive</p>
        </div>

        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6 sticky top-0 z-10 bg-[#0a0a0a]/95 backdrop-blur-sm py-2 -mx-4 px-4 sm:static sm:bg-transparent sm:p-0 sm:m-0 sm:mb-6">
          {[
            { label: 'Registered', value: stats.total },
            { label: 'Checked in', value: stats.checked },
            { label: 'Pending', value: stats.pending },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 px-3 py-3.5 text-center">
              <p className="text-2xl font-semibold tabular-nums">{s.value}</p>
              <p className="spec-label mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="relative mb-4">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-600" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search owner, vehicle, or class…"
            className="input w-full pl-10 pr-4 py-3.5 min-h-[48px] bg-[#0a0a0a] border border-white/[0.08] text-base focus:ring-orange-500/30 focus:border-orange-500/40"
            autoComplete="off"
          />
          {searching && (
            <p className="text-xs text-gray-500 mt-1" role="status">Searching…</p>
          )}
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-700 text-red-300 rounded-lg px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}

        {rows.length === 0 && !searching ? (
          <div className="rounded-xl border border-dashed border-white/[0.08] py-16 text-center">
            <p className="text-gray-400 text-sm">
              {debouncedQuery ? 'No registrations match your search.' : 'No registrations yet for this event.'}
            </p>
          </div>
        ) : (
          <ul className="space-y-3 pb-8">
            {rows.map((row) => {
              const isBusy = busyId === row.id;
              const statusStyle = STATUS_STYLES[row.status] || STATUS_STYLES.submitted;
              return (
                <li
                  key={row.id}
                  className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/50 p-4 sm:p-5"
                >
                  <div className="flex flex-col gap-4">
                    <div>
                      <p className="font-semibold text-white text-base sm:text-lg leading-snug">{row.vehicle_name}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        {row.owner_name || row.owner_username}
                      </p>
                      {row.class_name && (
                        <p className="text-xs text-gray-600 mt-1 uppercase tracking-wider">{row.class_name}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${statusStyle}`}>
                        {STATUS_LABELS[row.status] || row.status}
                      </span>
                      {row.status === 'checked_in' && (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="min-h-[44px]"
                          disabled={isBusy}
                          onClick={() => handleStatus(row.id, 'confirmed')}
                        >
                          Undo check-in
                        </Button>
                      )}
                      {row.status !== 'checked_in' && row.status !== 'withdrawn' && (
                        <div className="flex gap-2 w-full sm:w-auto">
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            className="flex-1 sm:flex-none min-h-[44px] text-base"
                            disabled={isBusy}
                            onClick={() => handleStatus(row.id, 'checked_in')}
                          >
                            {isBusy ? '…' : 'Check in'}
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="flex-1 sm:flex-none min-h-[44px]"
                            disabled={isBusy}
                            onClick={() => handleStatus(row.id, 'no_show')}
                          >
                            No-show
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default EventCheckin;
